import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './StudyGuide.css';

function StudyGuide({ content, passage }) {
  // All hooks at the top!
  const [mode, setMode] = useState('reading'); // 'reading' or 'practice' (removed 'preview')
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [markingResults, setMarkingResults] = useState({});
  // Add a warning state for incomplete answers
  const [incompleteWarning, setIncompleteWarning] = useState(false);
  // Add refs for auto-scroll to unanswered questions
  const questionRefs = React.useRef({});
  // Add loading state for marking
  const [isMarking, setIsMarking] = useState(false);
  // Add ref for progress bar
  const progressBarRef = React.useRef(null);
  const MARKING_TIMEOUT_MS = 60000;
  const { i18n } = useTranslation();

  React.useEffect(() => {
    setAnsweredCount(Object.keys(userAnswers).filter(q => userAnswers[q] && userAnswers[q].toString().trim() !== '').length);
  }, [userAnswers]);

  if (!content || typeof content !== 'string' || !content.trim()) {
    return (
      <div className="study-guide">
        <div className="practice-mode-placeholder">
          <h3>No content available</h3>
          <p style={{ color: '#666', marginTop: '1em' }}>
            Please generate new content or try again.
          </p>
        </div>
      </div>
    );
  }

  // Aggressively clean up content
  let cleanedContent = content.toString()
    .replace(/\|?\s*Multiple Choice:\s*\|?/gi, '')
    .replace(/\|?\s*Short Answer:\s*\|?/gi, '')
    .replace(/\|/g, '');

  const markingSchemeMatch = cleanedContent.match(/(Marking Scheme[\s\S]*)/i);
  const markingScheme = markingSchemeMatch ? markingSchemeMatch[1] : '';
  const mainContent = markingSchemeMatch ? cleanedContent.replace(markingScheme, '') : cleanedContent;

  // Function to extract reading passage from content
  function extractReadingPassage(content) {
    const lines = content.split('\n');
    const passageLines = [];
    let i = 0;
    
    // Skip until we find the first question (starts with a number followed by a dot)
    while (i < lines.length) {
      const line = lines[i].trim();
      // Check if this line starts a question (number followed by dot)
      if (/^\d+\./.test(line)) {
        break; // Found first question, stop here
      }
      // Check if this line contains section headings or other structural elements
      if (line && !/^📚\s*Section\s*\d+:/.test(line)) {
        passageLines.push(line);
      }
      i++;
    }
    
    // Remove "Questions:" from the end if it exists
    let passage = passageLines.join('\n').trim();
    passage = passage.replace(/\*\*Questions:\*\*?\s*$/i, '').trim();
    
    return passage;
  }

  // Extract reading passage
  const readingPassage = extractReadingPassage(mainContent);
  
  function getMarkingPassage() {
    const raw = (passage || readingPassage || mainContent || '').toString().trim();
    if (raw.length <= 12000) return raw;
    return `${raw.slice(0, 9000)}\n...\n${raw.slice(-2500)}`;
  }

  // Remove the requirement for a Marking Scheme section
  // if (!markingScheme || !markingScheme.trim()) {
  //   return (
  //     <div className="study-guide">
  //       <div className="practice-mode-placeholder">
  //         <h3>Missing Marking Scheme</h3>
  //         <p style={{ color: '#666', marginTop: '1em' }}>
  //           The content is missing a marking scheme section. Please try generating new content.
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  if (!mainContent || !mainContent.trim()) {
    return (
      <div className="study-guide">
        <div className="practice-mode-placeholder">
          <h3>Missing Questions</h3>
          <p style={{ color: '#666', marginTop: '1em' }}>
            The content is missing questions. Please try generating new content.
          </p>
        </div>
      </div>
    );
  }

  // Function to render reading passage
  function renderReadingPassage() {
    if (!readingPassage || !readingPassage.trim()) {
      return (
        <div className="reading-passage-placeholder">
          <h3>No reading passage available</h3>
          <p style={{ color: '#666', marginTop: '1em' }}>
            The content does not contain a reading passage. Please try generating new content.
          </p>
        </div>
      );
    }
    
    return (
      <div className="reading-passage-content">
        <h3>Reading Passage</h3>
        <div className="passage-text">
          {readingPassage.split('\n').map((line, index) => (
            <p key={index} style={{ marginBottom: '1em', lineHeight: '1.6' }}>
              {line}
            </p>
          ))}
        </div>
        
        {/* Question-Answer Book Toggle at the end of passage */}
        <div className="passage-toggle-container">
          <button
            className="mode-toggle-btn active"
            onClick={() => {
              setMode('practice');
              // Scroll to the content container with proper spacing above navbar
              setTimeout(() => {
                const contentContainer = document.querySelector('.study-guide');
                if (contentContainer) {
                  contentContainer.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                  });
                }
              }, 100);
            }}
          >
            <span className="page-emoji">📝</span>
            Question-Answer Book: Practice Mode
          </button>
        </div>
      </div>
    );
  }

  // --- Enhanced Practice Mode logic start ---
  // Add state for showing model answers and progress
  // const [showModelAnswers, setShowModelAnswers] = useState({});
  // const [answeredCount, setAnsweredCount] = useState(0);

  // Update answeredCount whenever userAnswers changes
  // React.useEffect(() => {
  //   setAnsweredCount(Object.keys(userAnswers).filter(q => userAnswers[q] && userAnswers[q].toString().trim() !== '').length);
  // }, [userAnswers]);

  function renderProgressBar() {
    if (!questions || questions.length === 0) return null;
    const percent = Math.round((answeredCount / questions.length) * 100);
    return (
      <div className="practice-progress-bar-container" ref={progressBarRef} style={{marginBottom: '1.5em'}}>
        <div className="practice-progress-bar-bg">
          <div className="practice-progress-bar-fill" style={{width: `${percent}%`}}></div>
        </div>
        <div className="practice-progress-bar-label">{answeredCount} / {questions.length} answered</div>
      </div>
    );
  }

  // Parse all question types from mainContent
  function parseQuestions(content) {
    const lines = content.split('\n');
    const questions = [];
    let i = 0;
    while (i < lines.length) {
      let line = lines[i].trim();
      const mcqMatch = line.match(/^(\d+)\.\s*(.*)$/);
      if (mcqMatch) {
        const qNum = mcqMatch[1];
        const qText = mcqMatch[2];
        if (/True\/False\/Not Given/i.test(qText)) {
          questions.push({ qNum, qText, type: 'truefalse', options: ['True', 'False', 'Not Given'] });
          i++;
        } else {
          const options = [];
          let j = i + 1;
          while (j < lines.length) {
            const optMatch = lines[j].trim().match(/^([A-D])\.\s*(.*)$/);
            if (optMatch) {
              options.push({ label: optMatch[1], text: optMatch[2] });
              j++;
            } else {
              break;
            }
          }
          if (options.length > 0) {
            questions.push({ qNum, qText, type: 'mcq', options });
            i = j;
          } else {
            questions.push({ qNum, qText, type: 'short' });
            i++;
          }
        }
      } else {
        i++;
      }
    }
    return questions;
  }

  // Parse marking scheme for all types
  function parseAnswers(scheme) {
    const lines = scheme.split('\n');
    const answers = {};

    lines.forEach(line => {
      const trimmedLine = line.trim();

      // Flexible: Accept Model Answer X:, Question X:, QX:, X. as possible answers
      const modelMatch = trimmedLine.match(/^(?:Model\s*Answer|Question|Q)?\s*(\d+)[.:)?]?\s*(.*)$/i);
      if (modelMatch) {
        const qNum = modelMatch[1];
        let modelAnswerText = modelMatch[2].trim();
        // Heuristic: skip if answer contains rubric/criteria words and does not look like a plausible answer
        // Accept if it looks like a letter (A-D), True/False/Not Given, or a sentence (has a verb or is long enough)
        const rubricPattern = /mark|criteria|correct|point|guideline|keyword/i;
        const plausibleMCQ = /^[A-D]\.?$/i.test(modelAnswerText);
        const plausibleTF = /^(True|False|Not Given)\b/i.test(modelAnswerText);
        const plausibleSentence = /[a-z]{3,}/i.test(modelAnswerText) && /[.?!]$/.test(modelAnswerText);
        if (rubricPattern.test(modelAnswerText) && !(plausibleMCQ || plausibleTF || plausibleSentence)) {
          return; // skip rubric/criteria lines
        }
        // Remove trailing (X marks) if present
        modelAnswerText = modelAnswerText.replace(/\s*\(\d+\s*marks?\)\s*$/i, '').trim();
        if (!answers[qNum]) answers[qNum] = {};
        answers[qNum].modelAnswer = modelAnswerText;
        // Try to extract marks
        const marksMatch = trimmedLine.match(/\((\d+)\s*marks?\)/i);
        const marks = marksMatch ? parseInt(marksMatch[1], 10) : null;
        answers[qNum].marks = marks;
        return;
      }

      // Guideline (optional)
      const guidelineMatch = trimmedLine.match(/^Guideline\s*(\d+):\s*(.*)/i);
      if (guidelineMatch) {
        const qNum = guidelineMatch[1];
        const guideline = guidelineMatch[2].trim();
        if (qNum) {
          if (!answers[qNum]) answers[qNum] = {};
          answers[qNum].guideline = guideline;
        }
        return;
      }
    });

    return answers;
  }

  // Enhanced normalization: aggressively convert Q1:, 1., Question 1:, etc. to Model Answer [Number]: ...
  function normalizeMarkingScheme(scheme) {
    const lines = scheme.split('\n');
    const normalized = [];
    // Accept Q1:, 1., Question 1:, etc.
    const qPattern = /^[-*]?\s*(?:Q|Question|)(\d+)[.:]?\s*(.*)$/i;
    lines.forEach(line => {
      let trimmed = line.trim();
      // Convert Q1: ..., 1. ..., or Question 1: ... to Model Answer 1: ... if it looks like an answer
      const match = trimmed.match(qPattern);
      if (match && match[2] && !trimmed.startsWith('Model Answer') && !trimmed.startsWith('Guideline')) {
        normalized.push(`Model Answer ${match[1]}: ${match[2]}`);
      } else {
        normalized.push(line);
      }
    });
    return normalized.join('\n');
  }

  // --- Parse ONCE at the top level ---
  const normalizedMarkingScheme = normalizeMarkingScheme(markingScheme);
  const questions = parseQuestions(mainContent);
  const correctAnswers = parseAnswers(normalizedMarkingScheme);

  // Parse explicit AnswerKeyJSON if present
  let parsedKey = null;
  try {
    const m = content.match(/AnswerKeyJSON:\s*(\{.*?\})/);
    if (m && m[1]) {
      parsedKey = JSON.parse(m[1]);
    }
  } catch {}
  if (parsedKey && questions) {
    questions.forEach(q => {
      const v = parsedKey[String(q.qNum)];
      if (v && (q.type === 'mcq' || q.type === 'truefalse')) {
        correctAnswers[q.qNum] = correctAnswers[q.qNum] || {};
        correctAnswers[q.qNum].modelAnswer = v;
      }
    });
  }

  // Fallback inference: if an objective question is missing a model answer, try common patterns
  function inferObjectiveModelAnswer(qNum) {
    const patterns = [
      new RegExp(`(?:Model\\s*Answer|Answer|Correct\\s*Answer)\\s*${qNum}\\s*[:\-]?\\s*([A-D])\\b`, 'i'),
      new RegExp(`(?:Model\\s*Answer|Answer|Correct\\s*Answer)\\s*${qNum}\\s*[:\-]?\\s*(True|False|Not\\s*Given)`, 'i')
    ];
    for (const rx of patterns) {
      const m = normalizedMarkingScheme.match(rx);
      if (m && m[1]) return m[1].replace(/\s+/g, ' ').trim();
    }
    return null;
  }

  // Populate missing model answers for objective questions when possible
  questions?.forEach(q => {
    if ((q.type === 'mcq' || q.type === 'truefalse') && (!correctAnswers[q.qNum] || !correctAnswers[q.qNum].modelAnswer)) {
      const inferred = inferObjectiveModelAnswer(q.qNum);
      if (inferred) {
        correctAnswers[q.qNum] = correctAnswers[q.qNum] || {};
        correctAnswers[q.qNum].modelAnswer = inferred;
      }
    }
  });

  // Small concurrency runner
  async function runWithConcurrency(items, worker, limit) {
    const results = [];
    let index = 0;
    const runners = new Array(Math.min(limit, items.length)).fill(0).map(async function next() {
      while (index < items.length) {
        const current = items[index++];
        const r = await worker(current).catch(err => ({ err, current }));
        results.push(r);
      }
      return null;
    });
    await Promise.all(runners);
    return results;
  }

  // Robust all-answered check for all question types
  const allAnswered = questions && questions.length > 0 && questions.every(q => {
    const ans = userAnswers[q.qNum];
    if (q.type === 'mcq' || q.type === 'truefalse') {
      return ans && ans !== '';
    }
    // For short answer
    return ans && ans.toString().trim() !== '';
  });

  // --- User Feedback Loop: Regenerate button if marking scheme is missing or malformed ---
  // Remove marking scheme requirement for Practice Mode

  function handleChange(qNum, value) {
    setUserAnswers(prev => ({ ...prev, [qNum]: value }));
  }

  // Per-question retry for short answers (30s timeout)
  async function retryMarkQuestion(q) {
    const userAnswer = userAnswers[q.qNum] || '';
    if (!userAnswer.toString().trim()) return;
    const attemptOnce = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), MARKING_TIMEOUT_MS);
      const res = await fetch('/api/generate-marking', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passage: getMarkingPassage(),
          question: q.qText,
          student_answer: userAnswer,
          max_marks: correctAnswers[q.qNum]?.marks || 1,
          question_type: 'short_answer',
          language: i18n.language || 'en',
          dialect: (i18n.language || '').toLowerCase().startsWith('zh') ? 'cantonese' : ''
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      if (!data.success || !data.marking_result) throw new Error('Invalid response');
      return data.marking_result;
    };
    setIsMarking(true);
    try {
      let result;
      try { result = await attemptOnce(); }
      catch { await new Promise(r => setTimeout(r, 800)); result = await attemptOnce(); }
      setMarkingResults(prev => ({ ...prev, [q.qNum]: result }));
    } catch {
      setMarkingResults(prev => ({ ...prev, [q.qNum]: 'Correct: No\nFeedback: Sorry, we are unable to mark this question right now. Please try again later.' }));
    } finally { setIsMarking(false); }
  }

  async function handlePracticeSubmit(e) {
    e.preventDefault();
    if (showResults) return;
    if (!allAnswered) {
      setIncompleteWarning(true);
      if (progressBarRef.current) { progressBarRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      return;
    }
    setIncompleteWarning(false);
    setShowResults(true);
    setIsMarking(true);

    const newMarkingResults = {};
    const openEndedTasks = [];

    // Prepare tasks and do instant marking for objective
    for (const q of questions) {
      const userAnswer = userAnswers[q.qNum] || '';
      if (!userAnswer.toString().trim()) continue;
      const correct = correctAnswers[q.qNum]?.modelAnswer?.toString().trim();
      const isObjective = (q.type === 'mcq' || q.type === 'truefalse');
      if (isObjective && correct) {
        const isCorrect = userAnswer.toString().trim().toLowerCase() === correct.toLowerCase();
        newMarkingResults[q.qNum] = `Correct: ${isCorrect ? 'Yes' : 'No'}\nFeedback: ${isCorrect ? 'Great job! That matches the key.' : 'That does not match the key.'}`;
        if (!isCorrect && q.type === 'mcq') {
          openEndedTasks.push(q);
        }
      } else {
        // open-ended or missing key → send to API
        openEndedTasks.push(q);
      }
    }

    // Worker for marking with timeout + 1 retry
    const worker = async (q) => {
      const userAnswer = userAnswers[q.qNum];
      const attemptOnce = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), MARKING_TIMEOUT_MS);
        const isMCQ = q.type === 'mcq';
        let payload;
        if (isMCQ) {
          const optionsText = (q.options || []).map(opt => `${opt.label}. ${opt.text}`).join('\n');
          payload = {
            passage: getMarkingPassage(),
            question: q.qText,
            student_answer: userAnswer,
            max_marks: 1,
            question_type: 'mcq',
            options: optionsText,
            format: 'ABCD',
            language: i18n.language || 'en',
            dialect: (i18n.language || '').toLowerCase().startsWith('zh') ? 'cantonese' : ''
          };
        } else {
          payload = {
            passage: getMarkingPassage(),
            question: q.qText,
            student_answer: userAnswer,
            max_marks: correctAnswers[q.qNum]?.marks || 1,
            question_type: 'short_answer',
            language: i18n.language || 'en',
            dialect: (i18n.language || '').toLowerCase().startsWith('zh') ? 'cantonese' : ''
          };
        }
        const res = await fetch('/api/generate-marking', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json().catch(() => ({}));
        if (!data.success || !data.marking_result) throw new Error('Invalid response');
        return [q.qNum, data.marking_result];
      };
      try {
        try { return await attemptOnce(); }
        catch { await new Promise(r => setTimeout(r, 800)); return await attemptOnce(); }
      } catch (err) {
        const fallback = correctAnswers[q.qNum]?.modelAnswer ?
          `Correct: No\nFeedback: Sorry, we are unable to mark this question at the moment.\nModel Answer: ${correctAnswers[q.qNum].modelAnswer}` :
          'Correct: No\nFeedback: Sorry, we are unable to mark this question at the moment.';
        return [q.qNum, fallback];
      }
    };

    // Run with concurrency limit = 2
    const results = await runWithConcurrency(openEndedTasks, worker, 2);
    for (const entry of results) {
      if (!entry) continue;
      const [qNum, text] = entry;
      newMarkingResults[qNum] = text;
    }

    setMarkingResults(newMarkingResults);
    setIsMarking(false);
  }

  function renderPracticeMode() {
    try {
      // Only show error if there are no questions
      if (!questions || questions.length === 0) {
        return (
          <div className="practice-mode-placeholder">
            <h3>Unable to load practice questions</h3>
            <p>The content may not be formatted correctly. Try generating a new set.</p>
            <button className="cta-btn" onClick={()=>window.location.reload()}>Regenerate</button>
          </div>
        );
      }
      return (
        <form className="practice-mode-form" onSubmit={handlePracticeSubmit}>
          {renderProgressBar()}
          {incompleteWarning && (
            <div style={{ color: 'red', marginBottom: '1em', fontWeight: 600 }}>
              Please answer all questions before submitting.
            </div>
          )}
          {questions.map((q) => {
            const correctAnswer = correctAnswers[q.qNum];
            const isCorrect = correctAnswer && correctAnswer.modelAnswer && (q.type === 'mcq' || q.type === 'truefalse') && userAnswers[q.qNum]?.toString().trim().toLowerCase() === correctAnswer.modelAnswer?.toString().trim().toLowerCase();
            return (
              <div key={q.qNum} className="practice-question-block" ref={el => questionRefs.current[q.qNum] = el}>
                <p className="exam-question">{`${q.qNum}. ${q.qText}`}</p>
                {/* Show correct/incorrect assessment with emoji for MCQ/TrueFalse questions */}
                {showResults && markingResults[q.qNum] && (q.type === 'mcq' || q.type === 'truefalse') && (
                  <div className="assessment-indicator">
                    {markingResults[q.qNum].match(/Correct: (Yes|No)/i)?.[1]?.toLowerCase() === 'yes' ? (
                      <span className="correct-indicator">✅ Correct</span>
                    ) : (
                      <span className="incorrect-indicator">❌ Incorrect</span>
                    )}
                  </div>
                )}
                {q.type === 'mcq' && (
                  <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                    {q.options.map(opt => (
                      <li key={opt.label} style={{ marginBottom: '0.5em' }}>
                        <label>
                          <input type="radio" name={`q${q.qNum}`} value={opt.label} checked={userAnswers[q.qNum] === opt.label} onChange={() => handleChange(q.qNum, opt.label)} disabled={showResults} />
                          {' '}{opt.label}. {opt.text}
                          {showResults && correctAnswer?.modelAnswer === opt.label && <span style={{ color: 'green', marginLeft: 8 }}>(Correct)</span>}
                          {showResults && userAnswers[q.qNum] === opt.label && !isCorrect && <span style={{ color: 'red', marginLeft: 8 }}>(Your answer)</span>}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                {q.type === 'truefalse' && (
                  <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                    {q.options.map(opt => (
                      <li key={opt} style={{ marginBottom: '0.5em' }}>
                        <label>
                          <input type="radio" name={`q${q.qNum}`} value={opt} checked={userAnswers[q.qNum] === opt} onChange={() => handleChange(q.qNum, opt)} disabled={showResults} />
                          {' '}{opt}
                          {showResults && correctAnswer?.modelAnswer === opt && <span style={{ color: 'green', marginLeft: 8 }}>(Correct)</span>}
                          {showResults && userAnswers[q.qNum] === opt && !isCorrect && <span style={{ color: 'red', marginLeft: 8 }}>(Your answer)</span>}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                {q.type === 'short' && (
                  <div style={{ marginBottom: '1em' }}>
                    <input type="text" name={`q${q.qNum}`} value={userAnswers[q.qNum] || ''} onChange={e => handleChange(q.qNum, e.target.value)} disabled={showResults} style={{ width: '100%', padding: '0.5em' }} placeholder="Type your answer..."/>
                    {/* Show feedback after submit */}
                    {showResults && (
                      <div className="short-answer-feedback">
                        {/* Show correct/incorrect assessment with emoji for short answers */}
                        {markingResults[q.qNum] && (
                          <div className="assessment-indicator" style={{marginTop: '0.5em', marginBottom: '0.5em'}}>
                            {markingResults[q.qNum].match(/Correct: (Yes|No)/i)?.[1]?.toLowerCase() === 'yes' ? (
                              <span className="correct-indicator">✅ Correct</span>
                            ) : (
                              <span className="incorrect-indicator">❌ Incorrect</span>
                            )}
                          </div>
                        )}
                        {/* Show model answer and feedback from LLM response only for short answers */}
                        {markingResults[q.qNum] && (
                          <div className="marking-feedback">
                            <div><strong>Feedback:</strong> {markingResults[q.qNum].match(/Feedback: (.*)/)?.[1] || 'No feedback available.'}</div>
                            <div className="model-answer-card">
                              <strong>Model answer:</strong> {
                                markingResults[q.qNum].match(/Model Answer: (.*)/)?.[1] || 
                                (correctAnswer?.modelAnswer ? correctAnswer.modelAnswer : <em>No model answer available.</em>)
                              }
                            </div>
                          </div>
                        )}
                        {correctAnswer?.guideline && <div><strong>Marking guideline:</strong> {correctAnswer.guideline}</div>}
                      </div>
                    )}
                  </div>
                )}
                {/* Show model answer card for MCQ/TrueFalse after submit (from marking scheme only, not for marking) */}
                {showResults && (q.type === 'mcq' || q.type === 'truefalse') && (
                  <div className="model-answer-card" style={{marginTop: '0.5em'}}>
                    <strong>Model answer:</strong> {correctAnswer?.modelAnswer || <em>No model answer available.</em>}
                  </div>
                )}
                {/* Show marking feedback for MCQ/TrueFalse questions only (short answers handled above) */}
                {showResults && markingResults[q.qNum] && (q.type === 'mcq' || q.type === 'truefalse') && (
                  <div className="marking-feedback">
                    <div><strong>Feedback:</strong> {markingResults[q.qNum].match(/Feedback: (.*)/)?.[1] || 'No feedback available.'}</div>
                  </div>
                )}
              </div>
            );
          })}
          {!showResults && <button type="submit" className="cta-btn" style={{ marginTop: '1.5em' }} disabled={!allAnswered}>Submit Answers</button>}
        </form>
      );
    } catch (error) {
      console.error('Error rendering practice mode:', error);
      return (
        <div className="practice-mode-placeholder">
          <h3>Error rendering practice mode.</h3>
          <p>There was an issue displaying the questions. Please try generating a new set.</p>
          <button className="cta-btn" onClick={()=>window.location.reload()}>Regenerate</button>
        </div>
      );
    }
  }
  // --- Enhanced Practice Mode logic end ---

  return (
    <div className="study-guide">
      {/* AI Marking Loading Popup */}
      {isMarking && (
        <div className="marking-loading-overlay">
          <div className="marking-loading-popup">
            <div className="loading-spinner"></div>
            <h3>🤖 PaperTips AI is marking your answers...</h3>
            <p>Please wait while our AI analyzes your responses and provides personalized feedback.</p>
          </div>
        </div>
      )}
      
      <div className="mode-toggle-container">
        <button
          className={`mode-toggle-btn${mode === 'reading' ? ' active' : ''}`}
          onClick={() => setMode('reading')}
        >
          <span className="page-emoji">📖</span>
          Reading Passages
        </button>
        <button
          className={`mode-toggle-btn${mode === 'practice' ? ' active' : ''}`}
          onClick={() => setMode('practice')}
        >
          <span className="page-emoji">📝</span>
          Question-Answer book: Practice Mode
        </button>
      </div>
      <div className="exam-content">
        {mode === 'reading' ? (
          renderReadingPassage()
        ) : (
          renderPracticeMode()
        )}
      </div>
      <div className="study-tips">
        <h3>💡 Study Tips</h3>
        <ul>
          <li>Review this guide regularly</li>
          <li>Take notes while studying</li>
          <li>Practice with past papers</li>
          <li>Join study groups</li>
        </ul>
      </div>
    </div>
  );
}

export default StudyGuide; 
